/**
 * Maneuver insight rules: aggregate per-boat means from maneuver rows,
 * then compare home vs leaders. Total-loss gaps are annotated with phase,
 * boat-speed, and accmax trim drivers (cant / CA1 / twist / jib).
 */

import {
  findHomeRow,
  fleetMedianForColumn,
  formatNumber,
  getRowValue,
  getTeamName,
  leaders,
  meaningfulGap,
  toFiniteNumber,
} from '../fleetStats';
import { buildGapDetail, buildRecommendation, sortFindings } from '../templates';
import type { Evidence, Finding, InsightRow, ManeuverInsightInput } from '../types';
import {
  MANEUVER_DRIVER_RULES,
  MANEUVER_TOTAL_RULES,
  type ManeuverDriverRule,
  type ManeuverMetricRule,
} from './maneuverRulebook';

const AGG_METRICS = [
  'Loss_total_tgt',
  'Loss_inv_tgt',
  'Loss_turn_tgt',
  'Loss_build_tgt',
  'Bsp_min_delta',
  'Bsp_min',
  'Overshoot_perc',
  'Accel_slope',
  'Decel_slope',
  'Turn_radius',
  'Mmg',
  'Cant_accmax',
  'Wing_ca1_accmax',
  'Wing_twist_accmax',
  'Jib_sheet_pct_accmax',
  'Jib_lead_ang_accmax',
  'Jib_cunno_load_accmax',
] as const;

export interface MetricDriver {
  rule: ManeuverDriverRule;
  ours: number;
  leaders: number;
  /** Gap magnitude in the "worse" direction (always >= 0 when weAreWorse). */
  severity: number;
  weAreWorse: boolean;
}

/** Mean each metric per boat from individual maneuver rows. */
export function aggregateManeuversByBoat(rows: InsightRow[]): InsightRow[] {
  const byTeam = new Map<string, { counts: Record<string, number>; sums: Record<string, number>; sample: InsightRow }>();

  for (const row of rows) {
    const team = getTeamName(row);
    if (!team) {continue;}
    const key = team.toLowerCase();
    let bucket = byTeam.get(key);
    if (!bucket) {
      bucket = { counts: {}, sums: {}, sample: row };
      byTeam.set(key, bucket);
    }
    for (const metric of AGG_METRICS) {
      const v = toFiniteNumber(getRowValue(row, metric));
      if (v == null) {continue;}
      bucket.sums[metric] = (bucket.sums[metric] ?? 0) + v;
      bucket.counts[metric] = (bucket.counts[metric] ?? 0) + 1;
    }
  }

  const out: InsightRow[] = [];
  for (const [, bucket] of byTeam) {
    const team = getTeamName(bucket.sample);
    const agg: InsightRow = { source_name: team, TEAM: team };
    for (const metric of AGG_METRICS) {
      const n = bucket.counts[metric] ?? 0;
      if (n > 0) {
        agg[metric] = (bucket.sums[metric] ?? 0) / n;
      }
    }
    out.push(agg);
  }
  return out;
}

function leaderMeanForMetric(
  leaderRows: { row: InsightRow }[],
  metric: string
): number | null {
  const vals = leaderRows
    .map((lr) => toFiniteNumber(getRowValue(lr.row, metric)))
    .filter((v): v is number => v != null);
  if (vals.length === 0) {return null;}
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/**
 * Rank drivers by how much worse (or better) we are vs leaders, scaled by each rule's threshold.
 * Prefer phase-loss drivers when severity is similar so "where" comes before "what".
 */
export function collectMetricDrivers(
  homeRow: InsightRow,
  leaderRows: { row: InsightRow }[],
  weCareAboutWorse: boolean,
  limit = 3
): MetricDriver[] {
  const scored: MetricDriver[] = [];

  for (const rule of MANEUVER_DRIVER_RULES) {
    const ours = toFiniteNumber(getRowValue(homeRow, rule.metric));
    const leadersAvg = leaderMeanForMetric(leaderRows, rule.metric);
    if (ours == null || leadersAvg == null) {continue;}

    if (rule.matchLeaders) {
      // Trim/setup: only cite when explaining extra loss and we differ from leaders.
      if (!weCareAboutWorse) {continue;}
      const absGap = Math.abs(ours - leadersAvg);
      if (absGap < rule.threshold) {continue;}
      scored.push({
        rule,
        ours,
        leaders: leadersAvg,
        severity: absGap / Math.max(rule.threshold, 1e-6),
        weAreWorse: true,
      });
      continue;
    }

    if (!rule.better) {continue;}
    const gapInfo = meaningfulGap(ours, leadersAvg, rule.threshold, rule.better);
    if (!gapInfo) {continue;}
    if (weCareAboutWorse && !gapInfo.weAreWorse) {continue;}
    if (!weCareAboutWorse && gapInfo.weAreWorse) {continue;}

    const severity = Math.abs(gapInfo.gap) / Math.max(rule.threshold, 1e-6);
    scored.push({
      rule,
      ours,
      leaders: leadersAvg,
      severity,
      weAreWorse: gapInfo.weAreWorse,
    });
  }

  scored.sort((a, b) => {
    const rank = (d: MetricDriver) => {
      if (d.rule.id.startsWith('loss_')) {return 0;} // phase first
      if (d.rule.matchLeaders) {return 2;} // trim after boat-speed execution
      return 1;
    };
    const rankA = rank(a);
    const rankB = rank(b);
    if (rankA !== rankB && Math.abs(a.severity - b.severity) < 0.2 * Math.max(a.severity, b.severity, 1)) {
      return rankA - rankB;
    }
    return b.severity - a.severity;
  });

  return scored.slice(0, limit);
}

function formatDriverCause(driver: MetricDriver): string {
  const u = driver.rule.unit ? ` ${driver.rule.unit}` : '';
  const cause = driver.weAreWorse ? driver.rule.causeLoss : driver.rule.causeGain;
  return `${cause} (${formatNumber(driver.ours)}${u} vs ${formatNumber(driver.leaders)}${u})`;
}

function buildDriverDetailSuffix(drivers: MetricDriver[], weAreWorse: boolean): string {
  if (drivers.length === 0) {return '';}
  const parts = drivers.map(formatDriverCause);
  if (weAreWorse) {
    return ` Driven by ${parts.join('; ')}.`;
  }
  return ` Helped by ${parts.join('; ')}.`;
}

function headlineWithDriver(
  typeLabel: string,
  rule: ManeuverMetricRule,
  weAreWorse: boolean,
  topDriver: MetricDriver | null
): string {
  const base = weAreWorse ? rule.headlineLoss : rule.headlineGain;
  if (!topDriver) {
    return `${typeLabel}: ${base}`;
  }
  const cause = weAreWorse ? topDriver.rule.causeLoss : topDriver.rule.causeGain;
  if (weAreWorse) {
    return `${typeLabel}: ${base} — mainly ${cause}`;
  }
  return `${typeLabel}: ${base} — mainly ${cause}`;
}

function driverEvidence(
  drivers: MetricDriver[],
  perBoat: InsightRow[]
): Evidence[] {
  return drivers.map((d) => ({
    metric: d.rule.metric,
    label: d.rule.label,
    unit: d.rule.unit,
    ours: d.ours,
    leaders: d.leaders,
    fleetMedian: fleetMedianForColumn(perBoat, d.rule.metric),
    // Match-leaders trim has no inherent direction; chips still need a BetterDirection.
    better: d.rule.better ?? 'higher',
  }));
}

/**
 * Total-loss finding annotated with phase / execution drivers (min BSP, overshoot, etc.).
 * Bare "you are worse" cards without a why are not emitted when we are worse and no drivers exist.
 */
function evaluateOutcomeWithDrivers(
  rule: ManeuverMetricRule,
  perBoat: InsightRow[],
  homeBoat: string,
  eventType: string
): Finding | null {
  if (perBoat.length < 3) {return null;}
  const homeRow = findHomeRow(perBoat, homeBoat);
  if (!homeRow) {return null;}

  const ascending = rule.better === 'lower';
  // Rank leaders by the outcome metric itself so the comparison set matches the claim.
  const leaderRows = leaders(perBoat, rule.metric, homeBoat, ascending);
  if (leaderRows.length === 0) {return null;}

  const ours = toFiniteNumber(getRowValue(homeRow, rule.metric));
  const leadersAvg = leaderMeanForMetric(leaderRows, rule.metric);
  if (ours == null || leadersAvg == null) {return null;}

  const gapInfo = meaningfulGap(ours, leadersAvg, rule.threshold, rule.better);
  if (!gapInfo) {return null;}

  const leaderTeams = leaderRows.map((r) => r.team);
  const typeLabel = eventType.charAt(0) + eventType.slice(1).toLowerCase();
  const drivers = collectMetricDrivers(homeRow, leaderRows, gapInfo.weAreWorse, 3);

  // A worse outcome with no explanatory drivers is not actionable — skip it.
  if (gapInfo.weAreWorse && drivers.length === 0) {return null;}

  const topDriver = drivers[0] ?? null;
  const gapDetail = buildGapDetail({
    metricLabel: rule.label,
    ours,
    leaders: leadersAvg,
    unit: rule.unit,
    leaderTeams,
    weAreWorse: gapInfo.weAreWorse,
    betterIsHigher: rule.better === 'higher',
  });

  const recommendationTemplate = topDriver
    ? topDriver.rule.recommendation
    : rule.recommendation;
  const recommendationValue = topDriver ? topDriver.leaders : leadersAvg;
  const recommendationUnit = topDriver ? topDriver.rule.unit : rule.unit;
  const recommendationLabel = topDriver ? topDriver.rule.label : rule.label;

  // Show me sorts by the top driver so the table highlights the "why", not just the symptom.
  const sortKey = topDriver?.rule.metric ?? rule.metric;
  const sortAscending = topDriver
    ? topDriver.rule.better === 'lower' || !!topDriver.rule.matchLeaders
    : ascending;

  return {
    id: `maneuver-${eventType.toLowerCase()}-${rule.id}`,
    area: 'maneuver',
    severity: gapInfo.weAreWorse ? 'loss' : 'gain',
    headline: headlineWithDriver(typeLabel, rule, gapInfo.weAreWorse, topDriver),
    detail: `${gapDetail}${buildDriverDetailSuffix(drivers, gapInfo.weAreWorse)}`,
    evidence: [
      {
        metric: rule.metric,
        label: rule.label,
        unit: rule.unit,
        ours,
        leaders: leadersAvg,
        fleetMedian: fleetMedianForColumn(perBoat, rule.metric),
        better: rule.better,
      },
      ...driverEvidence(drivers, perBoat),
    ],
    recommendation: buildRecommendation({
      template: recommendationTemplate,
      leaderTeams,
      metricLabel: recommendationLabel,
      leadersValue: recommendationValue,
      unit: recommendationUnit,
    }),
    showMe: {
      eventType,
      sortKey,
      sortDirection: sortAscending ? 'asc' : 'desc',
      highlightTeams: [homeBoat, ...leaderTeams],
    },
  };
}

export function buildManeuverFindings(
  input: ManeuverInsightInput,
  homeBoat: string
): Finding[] {
  const perBoat = aggregateManeuversByBoat(input.rows);
  if (perBoat.length < 3) {return [];}

  const findings: Finding[] = [];
  for (const rule of MANEUVER_TOTAL_RULES) {
    const f = evaluateOutcomeWithDrivers(rule, perBoat, homeBoat, input.eventType);
    if (f) {findings.push(f);}
  }

  return sortFindings(findings);
}

export function maneuverHomePresent(rows: InsightRow[], homeBoat: string): boolean {
  const target = homeBoat.trim().toLowerCase();
  return rows.some((r) => getTeamName(r).toLowerCase() === target);
}

/** Re-export for tests. */
export type { Evidence };
